// screen: Tenant shell (rails, switcher, palette)
import * as React from 'react';
import { Link, Outlet, createFileRoute, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { useLingui, Trans } from '@lingui/react/macro';
import { CalendarDays, LayoutDashboard, Library, FileSearch, Radio, MonitorPlay, PanelsTopLeft, BarChart3, Activity, Globe, HardDrive, Plug, Users, Settings, Search, LogOut, ChevronsUpDown, WifiOff, Menu, History, CircleHelp, LifeBuoy, Sparkles, Contact } from 'lucide-react';
import { Avatar, Badge, Button, Sheet, SheetContent, CommandDialog, CommandGroup, CommandItem, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, EmptyState, Skeleton, cn, useToast } from '@viewstream/ui';
import { useAuth, useInactivityWarning } from '~/app/auth';
import { TenantProvider, useTenant } from '~/app/tenant';
import { PlayerTenant } from '@viewstream/player-react';
import { useSse } from '~/app/useSse';
import { setTelemetryTenant } from '~/app/telemetry';
import type { RailItem } from '~/app/permissions';
import { ProductName, LocaleToggle } from '~/app/brand';
import { ThemeToggle } from '~/app/theme';
import { UploadQueueProvider, useUploads } from '~/app/upload/queue';
import { UploadDrawer } from '~/app/upload/Drawer';
import { NotificationBell } from '~/app/NotificationBell';
import { TenantBrandMark, TenantDefaultsEffect } from '~/app/Branding';
import { match, norm, usePaletteHits, type Hit } from '~/app/search';
import { useLocale } from '~/app/locale';
import { fmtDateTime } from '@viewstream/i18n';
import { ImpersonationBanner, ImpersonationPrompt } from '~/app/Impersonation';
import { TenantBanners } from '~/app/AdminP4';
import { PlanBanner } from '~/app/Plan';
import { HelpButton, useHelpPaletteHits, useHelpShortcut, useOpenHelp, useTopicLabels } from '~/help/nav';

export const Route = createFileRoute('/_layout')({ component: TenantLayout });

function TenantLayout() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const { me, loading } = useAuth();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const { t } = useLingui();
  React.useEffect(() => { setTelemetryTenant(slug); return () => setTelemetryTenant(null); }, [slug]);
  // Admin build: a customer (Studio) session is not an operator session — go through the Cloudflare Access exchange
  // instead of reusing it (admin-p6; it used to answer 403 on every admin call).
  const usable = !!me?.user && (__APP__ !== 'admin' || !!me.user.interhost_role);
  React.useEffect(() => {
    if (!loading && !usable) void navigate({ to: '/login', search: { next: window.location.pathname } });
  }, [loading, usable, navigate]);
  if (loading) return <ShellSkeleton />;
  if (!usable) return null;
  return (
    <TenantProvider slug={slug} noAccess={<NoAccess slug={slug} />} pending={<ShellSkeleton />}>
      <UploadQueueProvider tenant={slug}>
        {/* every player on the tenant's screens applies the tenant's player configuration (theme, skin, live UI…) */}
        <PlayerTenant tenant={slug} lang={locale}>
          <Shell />
          <UploadDrawer />
        </PlayerTenant>
      </UploadQueueProvider>
    </TenantProvider>
  );
  function NoAccess({ slug }: { slug: string }) {
    return (
      <main className="mx-auto mt-24 max-w-md p-6">
        <EmptyState title={t`No access to this tenant`} description={t`Your account is not a member of “${slug}”. Pick one of your tenants.`}
          actions={me!.tenants.map((x) => <Button key={x.id} variant="secondary" onClick={() => navigate({ to: '/t/$slug', params: { slug: x.slug } })}>{x.name}</Button>)} />
      </main>
    );
  }
}

const ICONS: Record<RailItem, React.ComponentType<{ className?: string }>> = {
  dashboard: LayoutDashboard, library: Library, archive: FileSearch, live: Radio, catchup: History, epg: CalendarDays, players: MonitorPlay, sites: PanelsTopLeft, ai: Sparkles, analytics: BarChart3, monitoring: Activity, delivery: Globe, storage: HardDrive, integrations: Plug, team: Users, viewers: Contact, settings: Settings,
};

function Shell() {
  const { t } = useLingui();
  const { tenant, rail } = useTenant();
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const sse = useSse(tenant.slug);
  const warn = useInactivityWarning();
  const toast = useToast();
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { locale } = useLocale();
  const helpLang = locale === 'en' ? 'en' : 'he';
  useHelpShortcut(tenant.slug, helpLang);   // "?" opens the manual at the current screen
  const onHelp = /^\/t\/[^/]+\/help(\/|$)/.test(pathname);
  // a channel's Catch-up tab lights the Catch-up entry (not Live)
  const onCatchupTab = useRouterState({ select: (s) => /\/live\/[^/]+$/.test(s.location.pathname) && (s.location.search as { tab?: string }).tab === 'catchup' });

  const labels: Record<RailItem, string> = {
    dashboard: t`Dashboard`, library: t`Library`, archive: t`Archive`, live: t`Live`, catchup: t`Catch-up`, epg: t`EPG`, players: t`Players`, sites: t`Sites`, ai: t`AI review`, analytics: t`Analytics`, monitoring: t`Monitoring & alerts`, delivery: t`Delivery`,
    storage: t`Storage`, integrations: t`Integrations`, team: t`Team`, viewers: t`Registered viewers`, settings: t`Settings`,
  };
  const hrefFor = (item: RailItem) => (item === 'dashboard' ? `/t/${tenant.slug}` : `/t/${tenant.slug}/${item}`);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPaletteOpen((o) => !o); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  React.useEffect(() => { if (warn) toast.push({ title: t`You will be signed out soon`, description: t`Move the mouse or press a key to stay signed in.` }); }, [warn, toast, t]);

  const [drawer, setDrawer] = React.useState(false);
  const menuBtn = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => { setDrawer(false); }, [pathname]);   // a navigation closes the drawer
  const railBody = (
    <>
      <div className="flex h-14 items-center px-4"><ProductName /></div>
      <TenantSwitcher />
      <TenantDefaultsEffect />
      <ul className="mt-2 flex-1 space-y-0.5 px-2">
        {rail.map((item) => {
          const Icon = ICONS[item];
          const href = hrefFor(item);
          const active = item === 'dashboard' ? pathname === href : item === 'catchup' ? pathname.startsWith(href) || onCatchupTab : item === 'live' ? pathname.startsWith(href) && !onCatchupTab : pathname.startsWith(href);
          return (
            <li key={item}>
              <Link to={href as never} aria-current={active ? 'page' : undefined} className={cn('flex items-center gap-3 rounded-md px-3 py-2 text-sm', active ? 'bg-primary/10 text-primary' : 'text-fg hover:bg-surface-2')}>
                <Icon className="size-4" aria-hidden /> {labels[item]}
              </Link>
            </li>
          );
        })}
        {__APP__ === 'admin' ? <AdminRail /> : null}
      </ul>
      <div className="space-y-0.5 border-t border-border p-2">
        <Link to={'/t/$slug/help' as never} params={{ slug: tenant.slug } as never} aria-current={onHelp ? 'page' : undefined} data-testid="nav-help"
          className={cn('flex items-center gap-3 rounded-md px-3 py-2 text-sm', onHelp ? 'bg-primary/10 text-primary' : 'text-fg hover:bg-surface-2')}>
          <CircleHelp className="size-4" aria-hidden /> {t`Help`}
        </Link>
        <Button variant="ghost" className="w-full justify-start" onClick={() => { setDrawer(false); setPaletteOpen(true); }}>
          <Search className="size-4" aria-hidden /> {t`Search`} <kbd className="ms-auto rounded border border-border px-1 text-xs text-fg-muted ltr-isolate">⌘K</kbd>
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-dvh">
      {/* ≥ 768 px: the rail as before. Narrower: a ☰ button in the header opens it as a drawer (Radix Dialog: focus trap,
          Esc, closes on navigation), from the inline-start edge — the right in Hebrew — so pages get the full width. */}
      <nav aria-label={t`Main`} className="sticky top-0 hidden h-dvh w-60 shrink-0 flex-col border-e border-border bg-surface md:flex print:hidden">
        {railBody}
      </nav>
      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent side="start" title={t`Menu`} closeLabel={t`Close menu`} className="w-72 max-w-[85vw] md:hidden" overlayClassName="md:hidden" bodyClassName="flex flex-col p-0" onCloseAutoFocus={(e) => { e.preventDefault(); menuBtn.current?.focus(); }}>
          <nav aria-label={t`Main`} className="flex min-h-0 flex-1 flex-col" data-testid="nav-drawer">
            {railBody}
          </nav>
        </SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center justify-between gap-2 border-b border-border bg-surface px-3 md:px-6 print:hidden">
          <div className="flex min-w-0 items-center gap-2 text-sm text-fg-muted" data-sse={sse}>
            <Button ref={menuBtn} variant="ghost" size="icon" className="md:hidden" aria-label={t`Open menu`} aria-expanded={drawer} onClick={() => setDrawer(true)} data-testid="nav-open">
              <Menu className="size-5" aria-hidden />
            </Button>
            {sse === 'paused' ? <Badge tone="warn"><WifiOff className="size-3" aria-hidden /> {t`Live updates paused`}</Badge> : null}
            {tenant.mode === 'cdn' ? <Badge tone="primary">{t`CDN-only`}</Badge> : (tenant.mode as string) === 'ai' ? <Badge tone="primary">{t`AI newsroom`}</Badge> : (tenant.mode as string) === 'drm' ? <Badge tone="primary">{t`DRM only`}</Badge> : null}
            {me?.user?.interhost_role ? (
              <Badge tone="primary" data-testid="operator-badge">
                {me.user.interhost_role === 'interhost_admin' ? t`Interhost admin` : t`Interhost ops`}
              </Badge>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <UploadIndicator />
            <HelpButton slug={tenant.slug} ui={helpLang} />
            <NotificationBell tenant={tenant.slug} />
            <ThemeToggle />
            <LocaleToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2" aria-label={t`Account menu`}>
                  <Avatar name={me?.user?.name || me?.user?.email || '?'} /> <span className="hidden max-w-40 truncate text-sm sm:inline">{me?.user?.name || me?.user?.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel><span className="ltr-isolate">{me?.user?.email}</span> · {me?.user?.interhost_role ? (me.user.interhost_role === 'interhost_admin' ? t`Interhost admin` : t`Interhost ops`) : tenant.role}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => void navigate({ to: '/t/$slug/help' as never, params: { slug: tenant.slug } as never })} data-testid="menu-help">
                  <CircleHelp className="size-4" aria-hidden /> {t`Help center`}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void navigate({ to: '/t/$slug/support', params: { slug: tenant.slug }, search: { from: pathname } } as never)} data-testid="menu-support">
                  <LifeBuoy className="size-4" aria-hidden /> {t`Contact support`}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => void logout().then(() => navigate({ to: '/login', search: { next: undefined } }))}>
                  <LogOut className="size-4 icon-dir" aria-hidden /> {t`Sign out`}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <TenantBanners tenant={tenant.slug} />
        <PlanBanner tenant={tenant.slug} />
        <ImpersonationBanner tenant={tenant} />
        <ImpersonationPrompt tenant={tenant} />
        <main id="main" className="min-w-0 flex-1 p-3 md:p-6">
          <Outlet />
        </main>
        <footer className="border-t border-border px-3 py-2 text-center text-xs text-fg-muted md:px-6 print:hidden" data-testid="app-version">
          <span className="ltr-isolate" title={`release ${__RELEASE__.slice(0, 8)}`}>ViewStream {__VS_VERSION__}</span>
        </footer>
      </div>
      <Palette open={paletteOpen} onOpenChange={setPaletteOpen} rail={rail} labels={labels} hrefFor={hrefFor} />
    </div>
  );
}

function TenantSwitcher() {
  const { t } = useLingui();
  const { me } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const tenants = me?.tenants ?? [];
  if (tenants.length <= 1) return <div className="px-4 py-2 text-sm font-medium"><TenantBrandMark /></div>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" className="mx-2 justify-between" aria-label={t`Switch tenant`}>
          <TenantBrandMark /> <ChevronsUpDown className="size-4 shrink-0 opacity-60" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>{t`Tenants`}</DropdownMenuLabel>
        {tenants.map((x) => (
          <DropdownMenuItem key={x.id} onSelect={() => navigate({ to: '/t/$slug', params: { slug: x.slug } })} aria-current={x.slug === tenant.slug ? 'true' : undefined}>
            {x.name} <span className="ms-auto text-xs text-fg-muted">{x.role}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Admin rail section — routes stubbed in apps/admin (task 6.3). Rendered only in the Admin build. */
function AdminRail() {
  const { t } = useLingui();
  const items: [string, string][] = [
    ['tenants', t`Tenants`], ['fleet', t`Fleet`], ['live-ops', t`Live ops`], ['pipeline', t`Pipeline`], ['delivery-ops', t`Delivery ops`],
    ['storage-ops', t`Storage ops`], ['insight-ops', t`Insight ops`], ['billing', t`Billing`], ['security', t`Security`], ['platform', t`Platform`],
  ];
  return (
    <>
      <li className="mt-4 px-3 text-xs font-semibold uppercase tracking-wide text-fg-muted"><Trans>Interhost</Trans></li>
      {items.map(([k, label]) => (
        <li key={k}><Link to={`/admin/${k}` as never} className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-fg hover:bg-surface-2">{label}</Link></li>
      ))}
    </>
  );
}

/** ⌘K palette: rail navigation plus assets, channels, clips and programmes (app/search.ts). */
function Palette({ open, onOpenChange, rail, labels, hrefFor }: { open: boolean; onOpenChange: (o: boolean) => void; rail: RailItem[]; labels: Record<RailItem, string>; hrefFor: (i: RailItem) => string }) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { locale, tz } = useLocale();
  const [q, setQ] = React.useState('');
  const helpLang = locale === 'en' ? 'en' : 'he';
  const helpHits = useHelpPaletteHits(q, helpLang);
  const openHelp = useOpenHelp(tenant.slug, helpLang);
  const topicLabel = useTopicLabels();
  React.useEffect(() => { if (!open) setQ(''); }, [open]);
  const { hits, loading } = usePaletteHits(tenant.slug, open, { library: rail.includes('library'), live: rail.includes('live'), epg: rail.includes('epg') });
  const found = React.useMemo(() => match(hits, q), [hits, q]);
  const nq = norm(q);
  const nav = rail.filter((i) => !nq || norm(labels[i]).includes(nq));
  const go = (to: string, search?: Record<string, string>) => { onOpenChange(false); void navigate({ to: to as never, search: search as never }); };
  const base = `/t/${tenant.slug}`;
  const when = (s?: string) => (s ? fmtDateTime(s, { locale, tz }, { dateStyle: 'short', timeStyle: 'short' }) : '');
  const groups: { kind: Hit['kind']; heading: string }[] = [
    { kind: 'channel', heading: t`Channels` }, { kind: 'asset', heading: t`Assets` }, { kind: 'clip', heading: t`Clips` }, { kind: 'programme', heading: t`Programmes` },
  ];
  const open1 = (h: Hit) => {
    if (h.kind === 'asset') go(`${base}/library/${h.id}`);
    else if (h.kind === 'channel') go(`${base}/live/${h.id}`);
    else if (h.kind === 'clip') go(`${base}/live/${h.channelId}`, { tab: 'clips' });
    else go(`${base}/epg`, { channel: h.channelId ?? '', day: h.at ? dayIn(h.at, tz) : '' });
  };
  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} label={t`Search`} placeholder={t`Search assets, clips, channels, settings…`}
      empty={loading ? t`Searching…` : t`No results`} search={q} onSearchChange={setQ} shouldFilter={false}>
      {nav.length ? (
        <CommandGroup heading={t`Go to`}>
          {nav.map((item) => (
            <CommandItem key={item} value={`nav:${item}`} onSelect={() => go(hrefFor(item))}>{labels[item]}</CommandItem>
          ))}
        </CommandGroup>
      ) : null}
      {helpHits.length ? (
        <CommandGroup heading={t`Help`}>
          {helpHits.map((h) => (
            <CommandItem key={`help:${h.key}`} value={`help:${h.key}`} onSelect={() => { onOpenChange(false); openHelp({ topic: h.topic, section: h.section }); }} data-testid="search-hit-help">
              <CircleHelp className="size-4 shrink-0 text-fg-muted" aria-hidden />
              <span className="truncate" dir="auto">{h.section ? h.label : topicLabel(h.topic)}</span>
              {h.sub ? <span className="ms-auto max-w-[50%] shrink-0 truncate text-xs text-fg-muted" dir="auto">{h.sub}</span> : null}
            </CommandItem>
          ))}
        </CommandGroup>
      ) : null}
      {groups.map((g) => {
        const rows = found.filter((h) => h.kind === g.kind);
        return rows.length ? (
          <CommandGroup key={g.kind} heading={g.heading}>
            {rows.map((h) => (
              <CommandItem key={`${h.kind}:${h.id}`} value={`${h.kind}:${h.id}`} onSelect={() => open1(h)} data-testid={`search-hit-${h.kind}`}>
                <span className="truncate" dir="auto">{h.title}</span>
                <span className="ms-auto shrink-0 text-xs text-fg-muted" dir="auto">{[h.sub, h.kind === 'programme' || h.kind === 'clip' ? when(h.at) : ''].filter(Boolean).join(' · ')}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null;
      })}
    </CommandDialog>
  );
}

/** YYYY-MM-DD of an instant in the tenant time zone (the EPG `day` search param). */
function dayIn(isoTs: string, tz: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(isoTs));
}

function ShellSkeleton() {
  return (
    <div className="flex min-h-dvh">
      <div className="w-60 border-e border-border p-4"><Skeleton className="h-8 w-40" /><Skeleton className="mt-6 h-6 w-32" /><Skeleton className="mt-2 h-6 w-28" /></div>
      <div className="flex-1 p-6"><Skeleton className="h-8 w-64" /><Skeleton className="mt-6 h-32 w-full" /></div>
    </div>
  );
}

/** Header chip while uploads run in the background (library spec "Upload drawer": transfers survive navigation). */
function UploadIndicator() {
  const { t } = useLingui();
  const { uploads, setOpen } = useUploads();
  const active = uploads.filter((u) => u.status === 'queued' || u.status === 'starting' || u.status === 'uploading' || u.status === 'completing').length;
  const attention = uploads.filter((u) => u.status === 'paused' || u.status === 'error' || u.status === 'needs-file').length;
  if (!active && !attention) return null;
  return (
    <Button variant="ghost" size="sm" onClick={() => setOpen(true)} data-testid="upload-indicator">
      {active ? <Badge tone="primary">{t`Uploading ${active}`}</Badge> : null}
      {attention ? <Badge tone="warn">{t`Uploads need attention: ${attention}`}</Badge> : null}
    </Button>
  );
}
